import { useEffect, useRef, useState } from 'react';
import { Button, Progress } from 'antd';
import {
  ClockCircleOutlined,
  PlayCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  StopOutlined,
  CloseOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { apiClient } from '../api/apiClient';

type QueueStatus = 'WAITING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
type QueueKind = 'LEAD_GEN' | 'FIND_MORE_LEADS' | 'FIND_MORE_CONTACTS';

interface QueueEntry {
  jobId: string;
  kind: QueueKind;
  status: QueueStatus;
  searchPrompt: string | null;
  position: number;
  queuedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  totalStored: number | null;
  pipelineStage?: string | null;     // SEARCHING/ENRICHING/VERIFYING/STORING — only for RUNNING
  statusMessage?: string | null;     // human text for RUNNING
}

const KIND_LABEL: Record<QueueKind, string> = {
  LEAD_GEN: 'Lead Generation',
  FIND_MORE_LEADS: 'Find More Leads',
  FIND_MORE_CONTACTS: 'Find More Contacts',
};

const POLL_MS = 3000;
const PEEK_DURATION_MS = 3000;

const isActive = (s: QueueStatus) => s === 'WAITING' || s === 'RUNNING';
const isFinished = (s: QueueStatus) => s === 'DONE' || s === 'FAILED' || s === 'CANCELLED';

// Time-based eased progress per stage so the bar keeps moving between real
// status updates (matches the LeadGenerationTab page bar). Each stage has a
// floor/ceiling and an expected duration; we compute elapsed time since the
// job started and ease from floor → ceiling using 1 - e^(-t/τ).
const STAGE_BUCKETS: Record<string, { floor: number; ceiling: number; expectedMs: number }> = {
  WAITING:   { floor: 10, ceiling: 19, expectedMs: 30_000 },
  SEARCHING: { floor: 20, ceiling: 49, expectedMs: 5 * 60_000 },
  ENRICHING: { floor: 50, ceiling: 69, expectedMs: 3 * 60_000 },
  VERIFYING: { floor: 70, ceiling: 84, expectedMs: 90_000 },
  STORING:   { floor: 85, ceiling: 94, expectedMs: 60_000 },
};
const stageFromEntry = (entry: QueueEntry): keyof typeof STAGE_BUCKETS => {
  const stage = entry.pipelineStage?.toUpperCase();
  if (stage && STAGE_BUCKETS[stage]) return stage as keyof typeof STAGE_BUCKETS;
  const m = (entry.statusMessage || '').toLowerCase();
  if (m.includes('storing') || m.includes('saving')) return 'STORING';
  if (m.includes('verifying') || m.includes('verify')) return 'VERIFYING';
  if (m.includes('enriching') || m.includes('enrich')) return 'ENRICHING';
  if (m.includes('searching') || m.includes('finding')) return 'SEARCHING';
  return 'WAITING';
};
const progressForRunning = (entry: QueueEntry): number => {
  const stageKey = stageFromEntry(entry);
  const { floor, ceiling, expectedMs } = STAGE_BUCKETS[stageKey];
  // Use the job's startedAt as the easing origin. Best-effort: if missing,
  // fall back to floor.
  if (!entry.startedAt) return floor;
  const elapsed = Date.now() - new Date(entry.startedAt).getTime();
  if (elapsed <= 0) return floor;
  const tau = expectedMs / 3;
  const easedFraction = 1 - Math.exp(-elapsed / tau);
  return Math.min(ceiling, Math.floor(floor + (ceiling - floor) * easedFraction));
};

export default function PipelineQueueTray() {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);
  const peekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenRef = useRef<Map<string, QueueStatus>>(new Map());

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const resp = await apiClient.get('/lead-generation/queue');
        if (!alive) return;
        const next: QueueEntry[] = resp.data?.data ?? [];

        // Detect new entries / status transitions for auto-peek
        const prev = seenRef.current;
        let hasNewArrival = false;
        let hasNewCompletion = false;
        for (const e of next) {
          const prevStatus = prev.get(e.jobId);
          if (!prevStatus && isActive(e.status)) hasNewArrival = true;
          if (prevStatus && isActive(prevStatus) && isFinished(e.status)) hasNewCompletion = true;
        }

        // Update seen map
        const nextSeen = new Map<string, QueueStatus>();
        for (const e of next) nextSeen.set(e.jobId, e.status);
        seenRef.current = nextSeen;

        setEntries(next);

        if ((hasNewArrival || hasNewCompletion) && !manualOverride) {
          setExpanded(true);
          if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
          peekTimerRef.current = setTimeout(() => setExpanded(false), PEEK_DURATION_MS);
        }
      } catch {/* swallow */}

      if (!alive) return;
      timer = setTimeout(tick, POLL_MS);
    };

    tick();

    // Listen for explicit enqueue events from the search/find-more flows so
    // the tray reacts instantly instead of waiting for the next poll tick
    const onEnqueue = () => { if (alive) tick(); };
    window.addEventListener('pipeline-queue-enqueued', onEnqueue);

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
      window.removeEventListener('pipeline-queue-enqueued', onEnqueue);
    };
  }, [manualOverride]);

  const handleToggle = () => {
    if (peekTimerRef.current) {
      clearTimeout(peekTimerRef.current);
      peekTimerRef.current = null;
    }
    setManualOverride(true);
    setExpanded(prev => !prev);
  };

  const handleStop = async (jobId: string) => {
    try {
      await apiClient.post(`/lead-generation/pipeline-cancel/${jobId}`);
      // Optimistic: mark CANCELLED locally; next poll will reconcile
      setEntries(prev => prev.map(e =>
        e.jobId === jobId ? { ...e, status: 'CANCELLED' as QueueStatus, finishedAt: new Date().toISOString() } : e
      ));
    } catch {/* swallow */}
  };

  const handleDismiss = async (jobId: string) => {
    setEntries(prev => prev.filter(e => e.jobId !== jobId));
    try {
      await apiClient.post(`/lead-generation/queue/${jobId}/dismiss`);
    } catch {/* swallow — next poll will reconcile */}
  };

  const handleDismissAll = async () => {
    setEntries(prev => prev.filter(e => isActive(e.status)));
    try {
      await apiClient.post('/lead-generation/queue/dismiss-all-done');
    } catch {/* swallow */}
  };

  if (entries.length === 0) return null;

  const activeCount = entries.filter(e => isActive(e.status)).length;
  const doneCount = entries.length - activeCount;

  return (
    <div
      style={{
        position: 'fixed',
        top: 72,
        right: 16,
        width: 320,
        maxWidth: 'calc(100vw - 32px)',
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        zIndex: 1050,
        fontSize: 13,
      }}
    >
      {/* Header */}
      <div
        onClick={handleToggle}
        style={{
          background: '#005b96',
          color: '#fff',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {activeCount > 0 && `${activeCount} active`}
          {activeCount > 0 && doneCount > 0 && ' · '}
          {doneCount > 0 && `${doneCount} done`}
        </span>
        {expanded ? <DownOutlined /> : <UpOutlined />}
      </div>

      {expanded && (
        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {entries.map(e => (
            <QueueRow
              key={e.jobId}
              entry={e}
              onStop={() => handleStop(e.jobId)}
              onDismiss={() => handleDismiss(e.jobId)}
            />
          ))}
          {doneCount > 0 && (
            <div style={{ padding: '8px 14px', textAlign: 'right', borderTop: '1px solid #f0f0f0' }}>
              <Button type="link" size="small" onClick={handleDismissAll}>
                Clear all done
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QueueRow({
  entry,
  onStop,
  onDismiss,
}: {
  entry: QueueEntry;
  onStop: () => void;
  onDismiss: () => void;
}) {
  const { kind, status, searchPrompt, position, errorMessage, totalStored } = entry;
  const active = isActive(status);

  let icon: React.ReactNode;
  let statusText: React.ReactNode;
  let statusColor = '#888';

  if (status === 'WAITING') {
    icon = <ClockCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />;
    statusText = position > 0 ? `Position ${position} in queue` : 'Queued';
  } else if (status === 'RUNNING') {
    icon = <PlayCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
    const pct = progressForRunning(entry);
    const stageLabel = entry.statusMessage || entry.pipelineStage || 'Running...';
    statusText = (
      <>
        <div style={{ color: '#888', fontSize: 11 }}>{stageLabel} · {pct}%</div>
        <Progress
          percent={pct}
          size="small"
          status="active"
          showInfo={false}
          strokeColor="#005b96"
          style={{ marginTop: 2, marginBottom: 0 }}
        />
      </>
    );
  } else if (status === 'DONE') {
    icon = <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />;
    statusText = totalStored != null ? `Done — ${totalStored} leads added` : 'Done';
    statusColor = '#52c41a';
  } else if (status === 'FAILED') {
    icon = <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />;
    statusText = errorMessage ? `Failed — ${errorMessage}` : 'Failed';
    statusColor = '#ff4d4f';
  } else {
    icon = <StopOutlined style={{ color: '#999', fontSize: 16 }} />;
    statusText = 'Cancelled';
  }

  return (
    <div
      style={{
        padding: '10px 14px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ paddingTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#333' }}>{KIND_LABEL[kind]}</div>
        {searchPrompt && (
          <div
            style={{
              color: '#888',
              fontSize: 11,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={searchPrompt}
          >
            {searchPrompt}
          </div>
        )}
        <div style={{ color: statusColor, fontSize: 11, marginTop: 2 }}>
          {statusText}
        </div>
      </div>
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined style={{ fontSize: 12 }} />}
        onClick={active ? onStop : onDismiss}
        title={active ? 'Stop' : 'Dismiss'}
      />
    </div>
  );
}
